import { Sparkles, Upload, Link2, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type TargetAcquisitionMode = "automatic" | "csv" | "sales_nav";

export const TARGET_MODE_LABELS: Record<TargetAcquisitionMode, string> = {
  automatic: "Describe",
  csv: "Upload a list",
  sales_nav: "Sales Navigator",
};

export const TARGET_MODE_STYLES: Record<TargetAcquisitionMode, { icon: typeof Sparkles; circle: string; ring: string; selected: string; badge: string }> = {
  automatic: { icon: Sparkles, circle: "bg-primary/10 text-primary", ring: "hover:border-primary", selected: "border-primary bg-primary/5", badge: "bg-primary text-primary-foreground" },
  csv: { icon: Upload, circle: "bg-sage-muted text-sage", ring: "hover:border-sage", selected: "border-sage bg-sage-muted/40", badge: "bg-sage text-primary-foreground" },
  sales_nav: { icon: Link2, circle: "bg-info-muted text-info", ring: "hover:border-info", selected: "border-info bg-info-muted/40", badge: "bg-info text-primary-foreground" },
};

interface TargetAcquisitionSelectorProps {
  selectedMode: TargetAcquisitionMode | null;
  onSelect: (mode: TargetAcquisitionMode) => void;
  uploadedTargetCount?: number;
  disabled?: boolean;
  summaries?: Partial<Record<TargetAcquisitionMode, string>>;
  compact?: boolean;
}

const modes: { id: TargetAcquisitionMode; line: string; }[] = [
  { id: "automatic", line: "Describe who, we find them" },
  { id: "csv", line: "Drop a file of LinkedIn links" },
  { id: "sales_nav", line: "Paste your search link" },
];

export const TargetAcquisitionSelector = ({ selectedMode, onSelect, disabled = false, summaries = {}, compact = false }: TargetAcquisitionSelectorProps) => (
  <div className={cn("grid grid-cols-1 sm:grid-cols-3", compact ? "gap-3" : "gap-4")}>
    {modes.map((m) => {
      const st = TARGET_MODE_STYLES[m.id];
      const Icon = st.icon;
      const isSel = selectedMode === m.id;
      const summary = summaries[m.id];
      return (
        <button
          key={m.id}
          type="button"
          disabled={disabled}
          aria-pressed={isSel}
          onClick={() => onSelect(m.id)}
          className={cn(
            "group relative flex items-center rounded-2xl border-2 border-border bg-card text-center shadow-sm transition-all duration-200",
            compact ? "flex-row gap-3 px-4 py-3 text-left" : "flex-col gap-3 px-6 pb-6 pt-8",
            "hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
            st.ring,
            isSel && st.selected,
            selectedMode && !isSel && "opacity-70 hover:opacity-100",
          )}
        >
          {isSel && (
            <span className={cn("absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full", st.badge)}>
              <Check className="h-3 w-3" />
            </span>
          )}
          <span className={cn("flex shrink-0 items-center justify-center rounded-2xl transition-transform group-hover:scale-105", st.circle, compact ? "h-10 w-10 rounded-xl" : "h-16 w-16")}>
            <Icon className={compact ? "h-5 w-5" : "h-7 w-7"} />
          </span>
          <span className="min-w-0 space-y-0.5">
            <span className="block text-base font-semibold text-foreground">{TARGET_MODE_LABELS[m.id]}</span>
            {selectedMode && <span className={cn("block truncate text-sm", summary ? "font-medium text-foreground/80" : "text-muted-foreground")}>
              {summary || m.line}
            </span>}
          </span>
        </button>
      );
    })}
  </div>
);
